import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { ADMIN_API, API } from '../constants/api';
import { AdminLoginRequest, AdminLoginResponse, LoginRequest, LoginResponse } from '../models/login.model';
import { SignupRequest } from '../models/signup.model';
import { User, UserRole } from '../models/user.model';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly api: ApiService,
    private readonly tokens: TokenStorageService,
  ) {}

  login(data: LoginRequest): Observable<LoginResponse> {
    return this.api.post<LoginResponse>(API.LOGIN, data).pipe(
      tap((response) => this.tokens.setToken(response.accessToken)),
    );
  }

  adminLogin(data: AdminLoginRequest): Observable<AdminLoginResponse> {
    this.tokens.clear();
    return this.api.post<AdminLoginResponse>(ADMIN_API.LOGIN, data).pipe(
      tap((response) => {
        if (response.status !== 1 || response.usertype?.toLowerCase() !== 'admin') {
          throw new Error(response.message || 'Admin login failed.');
        }
        if (!this.tokens.setToken(response.token, 'admin')) {
          throw new Error('The server returned an invalid admin session.');
        }
      }),
    );
  }

  signup(data: SignupRequest): Observable<User> {
    return this.api.post<User>(API.SIGNUP, data);
  }

  forgotPassword(email: string): Observable<void> {
    return this.api.post<void>('auth/forgot-password', { email });
  }

  verifyOtp(email: string, otp: string): Observable<void> {
    return this.api.post<void>('auth/verify-otp', { email, otp });
  }

  isAuthenticated(): boolean {
    return Boolean(this.tokens.getToken());
  }

  getRole(): UserRole | null {
    return this.tokens.getRole();
  }

  hasPermission(permission: string): boolean {
    const permissions = this.tokens.getPayload()?.['permissions'];
    return Array.isArray(permissions) && permissions.includes(permission);
  }

  logout(): void {
    this.tokens.clear();
  }
}
